import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  Globe,
  Zap,
  Lock,
  Check,
  ExternalLink,
  Wifi,
  Smartphone,
  EyeOff,
  Monitor,
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { productSchema } from '@/lib/seo';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';
import { useTranslation } from '@/lib/i18n';

const protocols = [
  'OpenVPN UDP/TCP',
  'IKEv2',
  'PPTP',
  'L2TP/IPsec',
];

const platforms = ['Windows', 'iOS', 'Android'];
const VPN_PRICE = '3.50';

export default function VPNPage() {
  const { t } = useTranslation();

  const vpnFeatures = [
    { icon: Zap,        key: 'vpn.feat_traffic' },
    { icon: Globe,      key: 'vpn.feat_locations' },
    { icon: Smartphone, key: 'vpn.feat_devices' },
    { icon: EyeOff,     key: 'vpn.feat_nologs' },
  ];

  const useCases = [
    { icon: Monitor, titleKey: 'vpn.uc1_title', descKey: 'vpn.uc1_desc' },
    { icon: Shield,  titleKey: 'vpn.uc2_title', descKey: 'vpn.uc2_desc' },
    { icon: EyeOff,  titleKey: 'vpn.uc3_title', descKey: 'vpn.uc3_desc' },
    { icon: Wifi,    titleKey: 'vpn.uc4_title', descKey: 'vpn.uc4_desc' },
  ];

  return (
    <Layout>
      <SEOHead
        title="VPN Servers – Secure & Private Internet"
        description="VPN servers for maximum privacy and security. Encrypted connection, no logs, unlimited bandwidth. Monthly cancellation, no contracts."
        canonical="/vpn"
        schema={productSchema({
          name: 'VPN',
          description: 'VPN for privacy and security – encrypted connection, no logs, unlimited bandwidth.',
          path: '/vpn',
          offers: [{ price: VPN_PRICE, period: 'month' }],
        })}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-primary">{t('vpn.badge_prepaid')}</Badge>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('vpn.title')} <span className="text-gradient">{t('vpn.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('vpn.subtitle')}
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3">
              {vpnFeatures.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass border border-border hover:border-primary/40 transition-colors"
                >
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-medium">{t(key)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Main Product Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-3xl mx-auto mb-20"
          >
            <div className="glass rounded-2xl overflow-hidden border border-primary/20">
              {/* Card Header */}
              <div className="bg-primary/10 border-b border-primary/20 px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <Lock className="w-6 h-6 text-primary" />
                    <h2 className="text-2xl font-black">VPN Service</h2>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t('vpn.plan_locations')} · {t('vpn.plan_devices')} · {t('vpn.plan_traffic')}
                  </p>
                </div>
                <div className="text-left sm:text-right shrink-0">
                  <div className="text-4xl font-black text-primary">€{VPN_PRICE}</div>
                  <div className="text-sm text-muted-foreground">{t('vpn.perMonth')}</div>
                </div>
              </div>

              {/* Card Body */}
              <div className="px-8 py-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Included Features */}
                  <div>
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">
                      {t('vpn.plan_protocols')}
                    </h3>
                    <ul className="space-y-3">
                      {[
                        t('vpn.plan_traffic'),
                        t('vpn.feat_nologs'),
                        t('vpn.feat_locations'),
                        t('vpn.feat_devices'),
                        'Apps: Windows, iOS, Android',
                        t('vpn.badge_prepaid'),
                      ].map((f) => (
                        <li key={f} className="flex items-center gap-3 text-sm">
                          <Check className="w-4 h-4 text-green-500 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Protocols */}
                  <div>
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-4">
                      {t('vpn.plan_protocols_supported')}
                    </h3>
                    <ul className="space-y-3">
                      {protocols.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-sm">
                          <Shield className="w-4 h-4 text-primary shrink-0" />
                          <span className="font-mono">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {platforms.map((p) => (
                      <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                    ))}
                  </div>
                  <a
                    href="https://deinserverhost.de/store/cart.php?a=add&pid=434"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="vpn-order-btn"
                  >
                    <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover font-bold uppercase tracking-wider">
                      {t('vpn.orderNow')}
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Use Cases */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-center mb-10">{t('vpn.plansTitle')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((item, index) => (
                <motion.div
                  key={item.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.08 }}
                  className="glass rounded-xl p-6 text-center border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold mb-2 text-sm">{t(item.titleKey)}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(item.descKey)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-2">{t('vpn.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm">
              {t('vpn.ctaDesc')}
            </p>
            <a
              href="https://deinserverhost.de/store/cart.php?a=add&pid=434"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                {t('vpn.ctaBtn')}
                <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
